import React, { ReactElement, ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import cx from 'classnames';

/**
 * A bounded list that says so: it fades out at its bottom edge while there's more below, and the fade
 * goes as soon as you reach the end. Without it a cut-off row reads as the end of the list.
 *
 * The fade is a sibling of the scrolling element, not a child — inside it, `bottom-0` would pin to the
 * bottom of the content and scroll away with it instead of staying at the edge you're looking through.
 */
export const ScrollFade = ({
  children,
  className,
  wrapperClassName,
  fadeClassName,
}: {
  children: ReactNode;
  /** The scrolling element itself: its bounding height, border, padding. */
  className?: string;
  /** The positioning wrapper — usually how it takes its share of a flex column. */
  wrapperClassName?: string;
  /** The fade: the `from-*` surface it resolves to, and any rounding matching the list's own. */
  fadeClassName?: string;
}): ReactElement => {
  const ref = useRef<HTMLDivElement>(null);
  // Starts hidden: a list that doesn't overflow never shows one, and this is also the resting state
  // before the first measure.
  const [atEnd, setAtEnd] = useState(true);

  const measure = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    setAtEnd(el.scrollHeight - el.scrollTop - el.clientHeight < 4);
  }, []);

  // Re-measure when the list itself resizes and when its content does — rows arrive after the first
  // paint, and a list that grew past its bound has a fade to show.
  useEffect(() => {
    const el = ref.current;
    measure();
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    Array.from(el.children).forEach(child => observer.observe(child));
    return () => observer.disconnect();
  }, [measure, children]);

  return (
    <div className={cx('relative', wrapperClassName)}>
      <div ref={ref} onScroll={measure} className={cx('overflow-y-auto', className)}>
        {children}
      </div>
      <div
        aria-hidden
        className={cx(
          // Inset by a pixel so a bordered list keeps its bottom border rather than having it washed out.
          'pointer-events-none absolute inset-x-px bottom-px h-8 bg-gradient-to-t to-transparent transition-opacity duration-200',
          fadeClassName ?? 'from-card',
          atEnd && 'opacity-0',
        )}
      />
    </div>
  );
};
